import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind classes with conflict resolution.
 *
 * `clsx` handles conditionals and `tailwind-merge` resolves collisions so a
 * caller-supplied `px-6` reliably beats a component's default `px-4` — without
 * it, both classes ship and the winner depends on stylesheet order.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format a number with locale-aware thousands separators. */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

/**
 * Human-readable relative time, e.g. "3 days ago".
 *
 * Uses `Intl.RelativeTimeFormat` so it localises automatically instead of
 * hard-coding English strings.
 */
export function formatRelativeTime(value: Date | string, now: Date = new Date()): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return 'unknown';

  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  const divisions: [limit: number, unit: Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [3600, 'minute'],
    [86_400, 'hour'],
    [604_800, 'day'],
    [2_629_800, 'week'],
    [31_557_600, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];

  let duration = seconds;
  let previousLimit = 1;
  for (const [limit, unit] of divisions) {
    if (Math.abs(seconds) < limit) {
      duration = seconds / previousLimit;
      return formatter.format(Math.round(duration), unit);
    }
    previousLimit = limit;
  }
  return formatter.format(Math.round(duration / previousLimit), 'year');
}

/**
 * Compact, human duration from a number of seconds, e.g. "3d 4h", "12m 5s".
 *
 * Shows at most two units: "3d 4h" is what someone wants from an uptime figure,
 * "3d 4h 12m 5s" is noise.
 */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));

  const units: [label: string, size: number][] = [
    ['d', 86_400],
    ['h', 3_600],
    ['m', 60],
    ['s', 1],
  ];

  const parts: string[] = [];
  let remaining = seconds;
  for (const [label, size] of units) {
    const amount = Math.floor(remaining / size);
    remaining -= amount * size;
    if (amount > 0 || parts.length > 0) parts.push(`${String(amount)}${label}`);
    if (parts.length === 2) break;
  }

  return parts.length > 0 ? parts.join(' ') : '0s';
}

/**
 * "supabase_postgrest" -> "Supabase Postgrest".
 *
 * Used to render machine identifiers (dependency names, enum values) in the UI
 * without maintaining a parallel table of display strings.
 */
export function titleCase(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Clamp a number into an inclusive range. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Initials for an avatar fallback, at most two characters. */
export function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  return `${(parts[0] ?? '').charAt(0)}${(parts.at(-1) ?? '').charAt(0)}`.toUpperCase();
}
